import { useCallback, useEffect, useRef, useState } from "react"

import JSON5 from "json5"

import { logger } from "@/lib/logger"

const log = logger("SessionStorage")
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue
    }

    try {
      const item = window.sessionStorage.getItem(key)
      return item ? JSON5.parse(item) : initialValue
    } catch (error) {
      log.warn(`Error reading sessionStorage key "${key}"`, { error, key })
      return initialValue
    }
  })

  // Use a ref to always access the latest storedValue without including it in dependencies
  const storedValueRef = useRef(storedValue)
  useEffect(() => {
    storedValueRef.current = storedValue
  }, [storedValue])

  // Return a wrapped version of useState's setter function that persists the new value to sessionStorage
  // Memoized with useCallback to ensure stable reference
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValueRef.current) : value
        setStoredValue(valueToStore)
        if (typeof window !== "undefined") {
          window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
        }
      } catch (error) {
        log.warn(`Error setting sessionStorage key "${key}"`, { error, key })
      }
    },
    [key],
  )

  return [storedValue, setValue]
}
